import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  Spinner,
  SimpleGrid,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  listPublicForms,
  clearFormFillerSession,
  getFormFillerToken,
} from '../../services/formService';

export default function PublicFormsHub() {
  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('#E6F5F3', 'gray.900');
  const textColor = useColorModeValue('gray.800', 'white');

  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [error, setError] = useState('');
  const [hasSession, setHasSession] = useState(Boolean(getFormFillerToken()));

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listPublicForms();
      if (res.data?.status === 'success') {
        setForms(res.data.data.forms || []);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'خطا در دریافت لیست فرم‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutSession = () => {
    clearFormFillerSession();
    setHasSession(false);
    toast.success('از این بخش خارج شدید');
  };

  return (
    <Flex minH="100vh" bg={pageBg} align="center" justify="center" px={4} py={8} dir="rtl">
      <Box w="100%" maxW="md" bg={cardBg} borderRadius="2xl" boxShadow="xl" p={{ base: 6, md: 8 }}>
        <Stack spacing={6}>
          <Box textAlign="center">
            <Heading size="lg" color={textColor} mb={2}>
              فرم‌های آنلاین
            </Heading>
            <Text fontSize="sm" color="gray.500">
              فرم موردنظر را انتخاب کنید؛ سپس برای تکمیل، شماره موبایل خود را تأیید می‌کنید.
            </Text>
          </Box>

          {error && (
            <Alert status="error" borderRadius="md" fontSize="sm">
              <AlertIcon />
              {error}
            </Alert>
          )}

          {hasSession && (
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <Text fontSize="sm" color="gray.600">
                موبایل تأییدشده: {localStorage.getItem('formFillerPhone') || '—'}
              </Text>
              <Button size="sm" variant="outline" onClick={handleLogoutSession}>
                خروج از این بخش
              </Button>
            </Flex>
          )}

          {loading ? (
            <Flex justify="center" py={8}>
              <Spinner color="teal.500" />
            </Flex>
          ) : forms.length === 0 ? (
            <Text textAlign="center" color="gray.500" py={6}>
              در حال حاضر فرم فعالی وجود ندارد.
            </Text>
          ) : (
            <SimpleGrid columns={1} spacing={3}>
              {forms.map((f) => (
                <Box
                  key={f._id}
                  as={Link}
                  to={`/auth/forms/${f._id}`}
                  p={4}
                  borderWidth="1px"
                  borderRadius="xl"
                  borderColor="gray.200"
                  _hover={{ borderColor: 'teal.400', bg: 'teal.50' }}
                  transition="all 0.2s"
                >
                  <Text fontWeight="bold" color={textColor}>
                    {f.title}
                  </Text>
                  {f.description ? (
                    <Text fontSize="sm" color="gray.600" noOfLines={2} mt={1}>
                      {f.description}
                    </Text>
                  ) : null}
                  <Text fontSize="xs" color="teal.600" mt={2}>
                    باز کردن فرم ←
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          )}

          <Button as={Link} to="/" variant="outline" size="md" w="full">
            بازگشت به صفحهٔ اصلی
          </Button>
        </Stack>
      </Box>
    </Flex>
  );
}
